/**
 * ResearchPropertyPage
 *
 * Page for researching property owners and managers for premises liability cases.
 * Allows users to input property owner/manager contact information which gets
 * stored on the coverage record.
 *
 * Workflow: Review Incident Properties
 * -> [Research Property & Owners] -> Write a Letter of Representation
 */
import {
  Building2,
  ChevronDown,
  ChevronRight,
  Loader2,
  Mail,
  MapPin,
  Phone,
  Plus,
  Trash2,
  User,
} from 'lucide-react';
import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import {
  emptyMailingAddress,
  isMailingAddressComplete,
  mailingAddressFromSources,
  mailingAddressHasAnyValue,
  normalizeMailingAddress,
} from '@/components/coverage/propertyContactAddress';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  useGetCoverageQuery,
  useUpdateCoverageMutation,
} from '@/services/api/coverageService';
import {
  ReadPropertyWithOwners,
} from '@/services/api/generated/subjectsService';
import { useAllSubjectsWithRelationshipsQuery } from '@/services/api/subjectsService';
import {
  useGetTasksQuery,
  useGetTaskStatusesQuery,
  useUpdateTaskMutation,
} from '@/services/api/tasksService';
import {
  MailingAddress,
  PropertyContact,
  PropertyContactType,
  PropertyRecipientCategory,
} from '@/services/types/coverage-types';
import {
  CanonicalTaskStatus,
  CanonicalTaskType,
} from '@/services/types/task-types';
import { pollForResult } from '@/utils/pollForResult';
import { parseAddressComponents } from '@/utils/providerDraft';

import { ClaimFilingTopHeader } from '../common/ClaimFilingTopHeader';
import {
  getCurrentPremisesFollowUpTask,
  getLatestPremisesSendLorTask,
  getPremisesFollowUpTaskType,
} from './premisesFlow';
import { PremisesStepper } from './PremisesStepper';

interface PropertyContactFormData {
  id: string;
  contact_type: PropertyContactType;
  custom_type_label?: string | null;
  name?: string | null;
  phone?: string;
  email?: string;
  mailing_address: MailingAddress;
  notes?: string;
  recipient_category?: PropertyRecipientCategory | null;
}

const PROPERTY_RESEARCH_RESOURCES = [
  {
    state: 'All States',
    purpose: 'Property Owner Lookup',
    resource: 'PropertyChecker',
    url: 'https://propertychecker.com',
    notes:
      'Free property search by address, owner name, or zip. Covers all 50 states including TX, MI, AZ, FL.',
  },
  {
    state: 'All States',
    purpose: 'Property Owner Lookup',
    resource: 'RealtyHop Property Records',
    url: 'https://www.realtyhop.com/property-records/search',
    notes:
      'Free property records by owner name, address, or organization. Strong coverage in TX, FL, MI, AZ.',
  },
  {
    state: 'All States',
    purpose: 'Property Owner Lookup',
    resource: 'County Assessor / Recorder',
    url: '',
    notes:
      "Most authoritative source. Search '[county name] property records' for local portal.",
  },
  {
    state: 'AZ',
    purpose: 'Registered Agent Lookup',
    resource: 'AZ Secretary of State',
    url: 'https://azsos.gov/business',
    notes:
      'Arizona SOS business entity database. Includes statutory agent and ownership details.',
  },
  {
    state: 'All States',
    purpose: 'Registered Agent Lookup',
    resource: 'SecStates.com',
    url: 'https://secstates.com',
    notes:
      'Aggregated links to all 50 state SOS business entity search pages. Good starting point for any state.',
  },
  {
    state: 'All States',
    purpose: 'Registered Agent Lookup',
    resource: 'National Corp Directory',
    url: 'https://nationalcorporatedirectory.com',
    notes:
      'Multi-state search across all 50 official SOS databases. $25/day for unlimited searches.',
  },
  {
    state: 'TX',
    purpose: 'Registered Agent Lookup',
    resource: 'TX SOS SOSDirect',
    url: 'https://sos.state.tx.us/corp/sosda/index.shtml',
    notes:
      'Official TX Secretary of State business entity search. $1 per search. Includes registered agent and management info.',
  },
  {
    state: 'FL',
    purpose: 'Registered Agent Lookup',
    resource: 'FL SunBiz',
    url: 'https://dos.fl.gov/sunbiz/search',
    notes:
      'Florida Division of Corporations official database. Free. Includes registered agent and officer details.',
  },
  {
    state: 'MI',
    purpose: 'Registered Agent Lookup',
    resource: 'MI LARA Business Registry',
    url: 'https://mibusinessregistry.lara.state.mi.us/search/business',
    notes:
      'Michigan Dept. of Licensing & Regulatory Affairs business entity search. Free.',
  },
  {
    state: 'NY',
    purpose: 'Property Owner Lookup',
    resource: 'Who Owns What (NYC)',
    url: 'https://whoownswhat.justfix.org/',
    notes:
      'Useful for NYC owner lookup by address and building information.',
  },
  {
    state: 'NY',
    purpose: 'Property Owner Lookup',
    resource: 'NYC Property Info Portal',
    url: 'https://propertyinformationportal.nyc.gov/',
    notes:
      'NYC property lookup portal with ownership and parcel information.',
  },
  {
    state: 'NY',
    purpose: 'Registered Agent Lookup',
    resource: 'NY DOS Entity Search',
    url: 'https://apps.dos.ny.gov/publicInquiry/EntityDisplay',
    notes:
      'New York Department of State entity lookup with registered-agent information.',
    },
];

const emptyContact = (): PropertyContactFormData => ({
  id: crypto.randomUUID(),
  contact_type: PropertyContactType.PROPERTY_OWNER,
  name: '',
  phone: '',
  email: '',
  mailing_address: emptyMailingAddress(),
  notes: '',
  recipient_category: null,
});

const getRecipientCategoryLabel = (
  category: PropertyRecipientCategory,
): string => {
  switch (category) {
    case PropertyRecipientCategory.CORPORATE_ENTITY:
      return 'Corporate Entity';
    case PropertyRecipientCategory.REGISTERED_AGENT:
      return 'Registered Agent';
    case PropertyRecipientCategory.PROPERTY_MANAGEMENT:
      return 'Property Management';
    case PropertyRecipientCategory.PRIVATE_RESIDENT:
      return 'Private Resident';
    case PropertyRecipientCategory.GOVERNMENT:
      return 'Government';
    default:
      return category;
  }
};

export const ResearchPropertyPage: React.FC = () => {
  const { matter_id: matterId, coverage_id: coverageId } = useParams<{
    matter_id: string;
    coverage_id: string;
  }>();
  const navigate = useNavigate();
  const [isResearchToolsOpen, setIsResearchToolsOpen] = React.useState(false);
  const [resourceStateFilter, setResourceStateFilter] = React.useState('');
  const [isRegenerating, setIsRegenerating] = React.useState(false);

  // Fetch the specific coverage by ID
  const { data: propertyCoverage } = useGetCoverageQuery(
    { matterId: matterId!, coverageId: coverageId! },
    { skip: !matterId || !coverageId },
  );

  const { data: subjectsData } = useAllSubjectsWithRelationshipsQuery(
    { matterId: matterId! },
    { skip: !matterId },
  );

  // Fetch tasks to find the research task for this coverage
  const {
    data: tasksResponse,
    refetch: refetchTasks,
  } = useGetTasksQuery(
    { matter: matterId!, limit: 200 },
    {
      skip: !matterId,
      refetchOnMountOrArgChange: true,
    },
  );

  const { data: taskStatuses = [], isLoading: isStatusesLoading } =
    useGetTaskStatusesQuery();

  const [updateCoverage, { isLoading: isUpdatingCoverage }] =
    useUpdateCoverageMutation();
  const [updateTask, { isLoading: isUpdatingTask }] = useUpdateTaskMutation();

  // Find the Research Property task for this specific coverage
  const researchTask = React.useMemo(() => {
    const tasks = tasksResponse?.results || [];
    return tasks.find(
      (t) =>
        t.type?.canonical_type === CanonicalTaskType.RESEARCH_PROPERTY_OWNERS &&
        t.coverage_id === coverageId,
    );
  }, [tasksResponse, coverageId]);

  const coverageTasks = React.useMemo(
    () => (tasksResponse?.results || []).filter((task) => task.coverage_id === coverageId),
    [tasksResponse, coverageId],
  );

  const currentPremisesFollowUpTask = React.useMemo(
    () => getCurrentPremisesFollowUpTask(coverageTasks),
    [coverageTasks],
  );

  const currentPremisesStep = React.useMemo(() => {
    const currentFollowUpTaskType = getPremisesFollowUpTaskType(
      currentPremisesFollowUpTask,
    );
    if (currentFollowUpTaskType === CanonicalTaskType.RESEARCH_ALTERNATE_CONTACTS) {
      return 'research_alternate_contacts';
    }
    return 'research_property';
  }, [currentPremisesFollowUpTask]);

  const isAlternateContactsStep =
    currentPremisesStep === 'research_alternate_contacts';

  const waitForResearchTask = React.useCallback(async () => {
    if (researchTask) return researchTask;

    return pollForResult({
      refetch: async () => (await refetchTasks()).data,
      predicate: (data) =>
        (data?.results || []).find(
          (t) =>
            t.type?.canonical_type === CanonicalTaskType.RESEARCH_PROPERTY_OWNERS &&
            t.coverage_id === coverageId,
        ) ?? null,
    });
  }, [coverageId, refetchTasks, researchTask]);

  const waitForRetrySendLorTask = React.useCallback(async () => {
    const existingId = getLatestPremisesSendLorTask(coverageTasks)?.id ?? null;

    return pollForResult({
      refetch: async () => (await refetchTasks()).data,
      predicate: (data) => {
        const tasks = (data?.results || []).filter(
          (t) => t.coverage_id === coverageId,
        );
        const latest = getLatestPremisesSendLorTask(tasks);
        return latest && latest.id !== existingId ? latest : null;
      },
      maxAttempts: 6,
    });
  }, [coverageId, coverageTasks, refetchTasks]);

  // Form state for property contacts
  const [contacts, setContacts] = React.useState<PropertyContactFormData[]>([
    emptyContact(),
  ]);

  // Initialize contacts from existing coverage data
  React.useEffect(() => {
    if (propertyCoverage?.property_contacts?.length) {
      setContacts(
        propertyCoverage.property_contacts.map((c) => {
          // Extract phone and email from handles array
          const phoneHandle = c.handles?.find((h) => h.handle_type === 'PHONE');
          const emailHandle = c.handles?.find((h) => h.handle_type === 'EMAIL');
          return {
            id: c.id || crypto.randomUUID(),
            contact_type: c.contact_type,
            custom_type_label: c.custom_type_label ?? null,
            name: c.name ?? null,
            phone: phoneHandle?.value || '',
            email: emailHandle?.value || '',
            mailing_address: mailingAddressFromSources(c.mailing_address),
            notes: c.notes || '',
            recipient_category: c.recipient_category ?? null,
          };
        }),
      );
    }
  }, [propertyCoverage]);

  const propertyMap = React.useMemo(() => {
    const map = new Map<string, ReadPropertyWithOwners>();
    subjectsData?.properties?.forEach((property) => {
      map.set(property.id as string, property);
    });
    return map;
  }, [subjectsData?.properties]);

  const formatAddress = (addr: ReadPropertyWithOwners['physical_address']) => {
    if (!addr) return null;

    const parts = [
      addr.line1,
      addr.line2,
      [addr.city, addr.state, addr.zip].filter(Boolean).join(', '),
    ].filter(Boolean);

    return parts.join(', ') || null;
  };

  const coverageAddress = React.useMemo(() => {
    if (!propertyCoverage?.property_id) {
      return propertyCoverage?.named_insured?.[0] ?? null;
    }

    const property = propertyMap.get(propertyCoverage.property_id);
    return (
      formatAddress(property?.physical_address) ??
      property?.property_name ??
      propertyCoverage.named_insured?.[0] ??
      null
    );
  }, [propertyCoverage, propertyMap]);

  const coverageMailingAddress = React.useMemo((): MailingAddress | null => {
    // 1. Try structured physical_address first
    if (propertyCoverage?.property_id) {
      const property = propertyMap.get(propertyCoverage.property_id);
      const addr = property?.physical_address;
      if (addr) {
        return {
          address_line1: addr.line1 || '',
          address_line2: addr.line2 || '',
          city: addr.city || '',
          state: addr.state || '',
          zip_code: addr.zip || '',
          country: 'US',
        };
      }
    }

    // 2. Fallback: parse the display string into components
    if (coverageAddress) {
      const parsed = parseAddressComponents(coverageAddress);
      if (parsed.addressLine1) {
        return {
          address_line1: parsed.addressLine1,
          address_line2: '',
          city: parsed.city,
          state: parsed.state,
          zip_code: parsed.postalCode,
          country: 'US',
        };
      }
    }

    return null;
  }, [propertyCoverage, propertyMap, coverageAddress]);

  const filteredResearchResources = React.useMemo(() => {
    const normalizedFilter = resourceStateFilter.trim().toLowerCase();
    return [...PROPERTY_RESEARCH_RESOURCES]
      .sort((left, right) => {
        const stateCompare = left.state.localeCompare(right.state);
        if (stateCompare !== 0) {
          return stateCompare;
        }
        return left.resource.localeCompare(right.resource);
      })
      .filter((resource) => {
        if (!normalizedFilter) {
          return true;
        }
        return (
          resource.state.toLowerCase().includes(normalizedFilter) ||
          resource.state === 'All States'
        );
      });
  }, [resourceStateFilter]);

  const handleAddContact = () => {
    setContacts((prev) => [...prev, emptyContact()]);
  };

  const handleRemoveContact = (id: string) => {
    setContacts((prev) => prev.filter((c) => c.id !== id));
  };

  const handleContactChange = (
    id: string,
    field: keyof PropertyContactFormData,
    value: string | null,
  ) => {
    setContacts((prev) =>
      prev.map((c) => (c.id === id ? { ...c, [field]: value } : c)),
    );
  };

  const handleMailingAddressChange = (
    id: string,
    field: keyof MailingAddress,
    value: string,
  ) => {
    setContacts((prev) =>
      prev.map((c) =>
        c.id === id
          ? {
              ...c,
              mailing_address: {
                ...c.mailing_address,
                [field]: value,
              },
            }
          : c,
      ),
    );
  };

  const handleUsePropertyAddress = (id: string) => {
    const nextMailingAddress = coverageMailingAddress ?? emptyMailingAddress();
    setContacts((prev) =>
      prev.map((c) =>
        c.id === id
          ? {
              ...c,
              mailing_address: nextMailingAddress,
            }
          : c,
      ),
    );
  };

  const handleSave = async (): Promise<boolean> => {
    if (!matterId || !coverageId) {
      toast.error('No property coverage found for this matter');
      return false;
    }

    // Validate recipient category is set for all named contacts
    const missingCategoryContact = contacts.find(
      (c) => c.name?.trim() && !c.recipient_category,
    );
    if (missingCategoryContact) {
      toast.error(
        `Select a recipient category for ${missingCategoryContact.name || 'this contact'}.`,
      );
      return false;
    }

    // Filter out empty contacts and format for API
    const invalidAddressContact = contacts.find(
      (c) =>
        c.name?.trim() &&
        mailingAddressHasAnyValue(c.mailing_address) &&
        !isMailingAddressComplete(c.mailing_address),
    );
    if (invalidAddressContact) {
      toast.error(
        `Complete the mailing address for ${invalidAddressContact.name || 'this contact'}. Street, city, state, and ZIP are required.`,
      );
      return false;
    }

    const validContacts: PropertyContact[] = contacts
      .filter((c) => c.name?.trim())
      .map((c) => ({
        id: c.id,
        contact_type: c.contact_type,
        custom_type_label:
          c.contact_type === PropertyContactType.OTHER
            ? c.custom_type_label
            : undefined,
        name: c.name?.trim() || '',
        handles: [
          c.phone?.trim()
            ? { handle_type: 'PHONE', value: c.phone.trim() }
            : null,
          c.email?.trim()
            ? { handle_type: 'EMAIL', value: c.email.trim() }
            : null,
        ].filter(Boolean) as PropertyContact['handles'],
        mailing_address: normalizeMailingAddress(c.mailing_address) || undefined,
        notes: c.notes?.trim() || undefined,
        recipient_category: c.recipient_category || undefined,
      }));

    try {
      await updateCoverage({
        matterId,
        coverageId,
        coverage: {
          property_contacts: validContacts,
        },
      }).unwrap();
      toast.success('Property contacts saved');
      return true;
    } catch (error) {
      console.error('Failed to save property contacts:', error);
      toast.error('Failed to save property contacts');
      return false;
    }
  };

  const handleMarkComplete = React.useCallback(async () => {
    if (!matterId) {
      toast.error('Matter ID not found');
      setIsRegenerating(false);
      return;
    }

    const taskToComplete = isAlternateContactsStep
      ? currentPremisesFollowUpTask
      : await waitForResearchTask();
    if (!taskToComplete) {
      if (isAlternateContactsStep) {
        toast.error('Could not find the alternate contacts task for this coverage.');
      } else {
        toast.info(
          'The next research task can take up to 20 seconds to appear after completing Review Incident Properties.',
          {
            duration: 10000,
            action: {
              label: 'Refresh',
              onClick: () => window.location.reload(),
            },
            description:
              'If it does not appear shortly, refresh the page and try again.',
          },
        );
      }
      setIsRegenerating(false);
      return;
    }

    const completedStatus = taskStatuses.find(
      (s) => s.canonical_status === CanonicalTaskStatus.COMPLETED,
    );

    if (!completedStatus) {
      toast.error('Could not find completed status. Please try again.');
      setIsRegenerating(false);
      return;
    }

    try {
      await updateTask({
        matterId,
        taskId: taskToComplete.id,
        task: {
          status_id: completedStatus.id,
          completed_at: new Date().toISOString(),
        },
      }).unwrap();

      if (isAlternateContactsStep) {
        const nextSendLorTask = await waitForRetrySendLorTask();
        toast.success(
          nextSendLorTask
            ? 'New LOR task created. Redirecting to draft the letter.'
            : 'Alternate contacts saved. Redirecting to the LOR step.',
        );
      } else {
        toast.success('Research task completed');
      }
      navigate(`/matters/${matterId}/coverage/${coverageId}/claims-review?step=lor`);
    } catch (error) {
      console.error('Failed to mark task complete:', error);
      toast.error('Failed to mark task complete');
      setIsRegenerating(false);
    }
  }, [
    coverageId,
    currentPremisesFollowUpTask,
    isAlternateContactsStep,
    matterId,
    navigate,
    taskStatuses,
    updateTask,
    waitForResearchTask,
    waitForRetrySendLorTask,
  ]);

  const handleSaveAndComplete = async () => {
    if (isAlternateContactsStep) setIsRegenerating(true);
    const saveSuccess = await handleSave();
    if (!saveSuccess) {
      setIsRegenerating(false);
      return;
    }
    await handleMarkComplete();
  };

  const isPrimaryActionDisabled =
    isUpdatingCoverage ||
    isUpdatingTask ||
    isStatusesLoading ||
    isRegenerating ||
    contacts.every((c) => !c.name?.trim());

  return (
    <div className="min-h-screen bg-zinc-50">
      <ClaimFilingTopHeader matterId={matterId!} />
      {coverageId && (
        <PremisesStepper
          matterId={matterId!}
          coverageId={coverageId}
          currentStep={currentPremisesStep}
          coverage={propertyCoverage}
        />
      )}

      <div className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        {/* Property Location Card */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <MapPin className="h-4 w-4 text-orange-600" />
              Property Location
            </CardTitle>
          </CardHeader>
          <CardContent>
            {coverageAddress ? (
              <p className="text-sm font-medium text-zinc-900">{coverageAddress}</p>
            ) : (
              <p className="text-sm text-zinc-500 italic">
                No property address is available for this coverage yet.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Research Brief */}
        {researchTask && (
          <Card className="border-blue-200 bg-blue-50/40">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Building2 className="h-4 w-4 text-blue-600" />
                {isAlternateContactsStep
                  ? 'Research Alternate Contacts'
                  : 'Research Property & Owners'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-zinc-700">
                {isAlternateContactsStep
                  ? 'Add new or corrected contacts for this property so a fresh LOR task can be generated for another send attempt.'
                  : 'Add the best available owner, manager, or responsible contact information below.'}
              </p>
              <Collapsible
                open={isResearchToolsOpen}
                onOpenChange={setIsResearchToolsOpen}
              >
                <div className="rounded-lg border border-blue-100 bg-white/80">
                  <CollapsibleTrigger asChild>
                    <button
                      type="button"
                      className="flex w-full items-center justify-between px-4 py-3 text-left"
                    >
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-blue-700">
                          Research Tools
                        </p>
                        <p className="mt-1 text-sm text-zinc-600">
                          Search owner and registered-agent sources by state.
                        </p>
                      </div>
                      {isResearchToolsOpen ? (
                        <ChevronDown className="h-4 w-4 text-zinc-500" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-zinc-500" />
                      )}
                    </button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="border-t border-blue-100 px-4 py-4">
                    <p className="mb-3 text-xs text-zinc-600">
                      Document which sources were used in the contact notes.
                    </p>
                    <div className="mb-3 max-w-xs">
                      <Input
                        value={resourceStateFilter}
                        onChange={(event) =>
                          setResourceStateFilter(event.target.value)
                        }
                        placeholder="Search state (e.g. NY, FL)"
                        className="h-8 text-xs"
                      />
                    </div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-xs">
                        <thead>
                          <tr className="border-b border-zinc-200 text-left text-[11px] uppercase tracking-wide text-zinc-500">
                            <th className="pb-2 pr-3 font-medium">State</th>
                            <th className="pb-2 pr-4 font-medium">Purpose</th>
                            <th className="pb-2 pr-4 font-medium">Resource</th>
                            <th className="pb-2 pr-4 font-medium">URL</th>
                            <th className="pb-2 font-medium">Notes</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-zinc-100">
                          {filteredResearchResources.map((resource) => (
                            <tr
                              key={`${resource.state}-${resource.purpose}-${resource.resource}`}
                              className="align-top"
                            >
                              <td className="py-2.5 pr-3 font-medium text-zinc-700 whitespace-nowrap">
                                {resource.state}
                              </td>
                              <td className="py-2.5 pr-4 font-medium text-zinc-800">
                                {resource.purpose}
                              </td>
                              <td className="py-2.5 pr-4 text-zinc-700">
                                {resource.resource}
                              </td>
                              <td className="py-2.5 pr-4">
                                {resource.url ? (
                                  <a
                                    href={resource.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:text-blue-800 underline underline-offset-2 break-all"
                                  >
                                    {resource.url.replace(/^https?:\/\//, '')}
                                  </a>
                                ) : (
                                  <span className="text-zinc-500">
                                    Search local county portal
                                  </span>
                                )}
                              </td>
                              <td className="py-2.5 text-zinc-600">{resource.notes}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CollapsibleContent>
                </div>
              </Collapsible>
            </CardContent>
          </Card>
        )}

        {/* Property Contacts Form */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base flex items-center gap-2">
                  <User className="h-4 w-4 text-violet-600" />
                  Add / Edit Contacts
                </CardTitle>
                <p className="text-xs text-zinc-500 mt-1">
                  Add property owners, managers, or other contacts who may have
                  insurance information.
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleAddContact}
                className="gap-1"
              >
                <Plus className="h-3.5 w-3.5" />
                Add Contact
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {contacts.map((contact) => (
              <div
                key={contact.id}
                className="border border-zinc-200 rounded-lg p-4 space-y-4 relative"
              >
                {contacts.length > 1 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveContact(contact.id)}
                    className="absolute top-2 right-2 h-7 w-7 p-0 text-zinc-400 hover:text-red-600"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}

                <div className="grid grid-cols-2 gap-4">
                  {/* Contact Type */}
                  <div className="space-y-2">
                    <Label className="text-xs font-medium text-zinc-700">
                      Contact Type
                    </Label>
                    <Select
                      value={contact.contact_type}
                      onValueChange={(value) =>
                        handleContactChange(
                          contact.id,
                          'contact_type',
                          value as PropertyContactType,
                        )
                      }
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={PropertyContactType.PROPERTY_OWNER}>
                          Property Owner
                        </SelectItem>
                        <SelectItem
                          value={PropertyContactType.PROPERTY_MANAGER}
                        >
                          Property Manager
                        </SelectItem>
                        <SelectItem value={PropertyContactType.OTHER}>
                          Other
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Recipient Category */}
                  <div className="space-y-2">
                    <Label className="text-xs font-medium text-zinc-700">
                      Recipient Category{' '}
                      <span className="text-rose-500">*</span>
                    </Label>
                    <Select
                      value={contact.recipient_category || '_none'}
                      onValueChange={(value) =>
                        handleContactChange(
                          contact.id,
                          'recipient_category',
                          value === '_none' ? null : value,
                        )
                      }
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="Select category..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_none">Not set</SelectItem>
                        {Object.values(PropertyRecipientCategory).map((category) => (
                          <SelectItem key={category} value={category}>
                            {getRecipientCategoryLabel(category)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Custom Type Label (for OTHER) */}
                  {contact.contact_type === PropertyContactType.OTHER && (
                    <div className="space-y-2">
                      <Label className="text-xs font-medium text-zinc-700">
                        Contact Role
                      </Label>
                      <Input
                        placeholder="e.g., Insurance Agent"
                        value={contact.custom_type_label || ''}
                        onChange={(e) =>
                          handleContactChange(
                            contact.id,
                            'custom_type_label',
                            e.target.value,
                          )
                        }
                        className="h-9"
                      />
                    </div>
                  )}

                  {/* Name */}
                  <div
                    className={`space-y-2 ${contact.contact_type !== PropertyContactType.OTHER ? 'col-span-1' : ''}`}
                  >
                    <Label className="text-xs font-medium text-zinc-700">
                      Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      placeholder="Contact name"
                      value={contact.name || ''}
                      onChange={(e) =>
                        handleContactChange(contact.id, 'name', e.target.value)
                      }
                      className="h-9"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  {/* Phone */}
                  <div className="space-y-2">
                    <Label className="text-xs font-medium text-zinc-700 flex items-center gap-1">
                      <Phone className="h-3 w-3" />
                      Phone
                    </Label>
                    <Input
                      placeholder="(555) 555-5555"
                      value={contact.phone || ''}
                      onChange={(e) =>
                        handleContactChange(contact.id, 'phone', e.target.value)
                      }
                      className="h-9"
                    />
                  </div>

                  {/* Email */}
                  <div className="space-y-2">
                    <Label className="text-xs font-medium text-zinc-700 flex items-center gap-1">
                      <Mail className="h-3 w-3" />
                      Email
                    </Label>
                    <Input
                      type="email"
                      placeholder="contact@example.com"
                      value={contact.email || ''}
                      onChange={(e) =>
                        handleContactChange(contact.id, 'email', e.target.value)
                      }
                      className="h-9"
                    />
                  </div>
                </div>

                {/* Mailing Address */}
                <div className="space-y-2">
                  <Label className="text-xs font-medium text-zinc-700 flex items-center gap-1">
                    <MapPin className="h-3 w-3" />
                    Mailing Address
                  </Label>
                  <div className="grid grid-cols-2 gap-4">
                    <Input
                      placeholder="Street address"
                      value={contact.mailing_address.address_line1 || ''}
                      onChange={(e) =>
                        handleMailingAddressChange(
                          contact.id,
                          'address_line1',
                          e.target.value,
                        )
                      }
                      className="h-9"
                    />
                    <Input
                      placeholder="Apt, suite, unit, etc. (optional)"
                      value={contact.mailing_address.address_line2 || ''}
                      onChange={(e) =>
                        handleMailingAddressChange(
                          contact.id,
                          'address_line2',
                          e.target.value,
                        )
                      }
                      className="h-9"
                    />
                    <Input
                      placeholder="City"
                      value={contact.mailing_address.city || ''}
                      onChange={(e) =>
                        handleMailingAddressChange(contact.id, 'city', e.target.value)
                      }
                      className="h-9"
                    />
                    <div className="grid grid-cols-2 gap-4">
                      <Input
                        placeholder="State"
                        value={contact.mailing_address.state || ''}
                        onChange={(e) =>
                          handleMailingAddressChange(contact.id, 'state', e.target.value)
                        }
                        className="h-9"
                        maxLength={2}
                      />
                      <Input
                        placeholder="ZIP"
                        value={contact.mailing_address.zip_code || ''}
                        onChange={(e) =>
                          handleMailingAddressChange(
                            contact.id,
                            'zip_code',
                            e.target.value,
                          )
                        }
                        className="h-9"
                      />
                    </div>
                  </div>
                  {coverageAddress && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-9 text-xs gap-1 text-violet-700 border-violet-200 hover:bg-violet-50"
                      onClick={() => handleUsePropertyAddress(contact.id)}
                    >
                      <MapPin className="h-3 w-3" />
                      Use property address
                    </Button>
                  )}
                  {mailingAddressHasAnyValue(contact.mailing_address) &&
                    !isMailingAddressComplete(contact.mailing_address) && (
                      <p className="text-xs text-rose-600">
                        Street, city, state, and ZIP are required for certified
                        mail.
                      </p>
                    )}
                </div>

                {/* Notes */}
                <div className="space-y-2">
                  <Label className="text-xs font-medium text-zinc-700">
                    Notes
                  </Label>
                  <p className="text-xs text-zinc-500">
                    Include any notes for this contact here, including where
                    the ownership or contact information was found.
                  </p>
                  <Textarea
                    placeholder="Add notes about this contact"
                    value={contact.notes || ''}
                    onChange={(e) =>
                      handleContactChange(contact.id, 'notes', e.target.value)
                    }
                    rows={2}
                    className="resize-none"
                  />
                </div>
              </div>
            ))}

            {contacts.length === 0 && (
              <div className="text-center py-8 border-2 border-dashed border-zinc-200 rounded-lg">
                <Building2 className="h-8 w-8 text-zinc-400 mx-auto mb-2" />
                <p className="text-sm text-zinc-600 mb-3">
                  No property contacts added yet
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleAddContact}
                  className="gap-1"
                >
                  <Plus className="h-3.5 w-3.5" />
                  Add Contact
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex items-center justify-between pt-4">
          {!isAlternateContactsStep && (
            <Button
              variant="outline"
              onClick={() => navigate(`/matters/${matterId}/coverage`)}
            >
              Cancel
            </Button>
          )}
          <div className={`flex items-center gap-3 ${isAlternateContactsStep ? 'ml-auto' : ''}`}>
            <Button
              variant="outline"
              onClick={handleSave}
              disabled={isUpdatingCoverage}
            >
              {isAlternateContactsStep ? 'Save Contact Info' : 'Save Contacts'}
            </Button>
            <Button
              onClick={handleSaveAndComplete}
              disabled={isPrimaryActionDisabled}
              className="bg-violet-600 hover:bg-violet-700"
            >
              {isRegenerating ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Regenerating...
                </>
              ) : isUpdatingTask ? (
                'Completing...'
              ) : isAlternateContactsStep ? (
                'Regenerate LOR Task'
              ) : (
                'Save & Complete Research'
              )}
            </Button>
          </div>
        </div>
      </div>

    </div>
  );
};
